import { aggregateByQuarter, legAirExcise } from "./calculator";
import type { Q4ComparisonTotals } from "./dunleavyQ4ComparisonReference";
import { DUNLEAVY_Q4_COMPARISON_RATES } from "./dunleavyQ4ComparisonReference";
import { filterQ4_2025TimeShareLegsFromLogbook } from "./dunleavyQ4LogbookLegs";
import { personalFuelOwed2x } from "./fuelDerived";
import { money } from "./money";
import {
  personalTransportPercentageTaxTotal,
  type PersonalTransport720Options,
} from "./personalTransport720";
import { totalExciseAir, type FlightLeg, type Rates } from "./types";

const personal720: PersonalTransport720Options = {
  basis: "fuel_cost_2x",
  isPersonalLeg: (l) => l.personalUse,
};

const emptyTotals = (): Q4ComparisonTotals => ({
  legCount: 0,
  domesticSegment: 0,
  internationalFacility: 0,
  combinedAirExcise: 0,
  twoXFuel: 0,
  optionalPersonalPctTax: 0,
  grandTotal: 0,
});

/** Quarter-style totals for a fixed leg list and rate table (Comparison tab building blocks). */
export function computeQ4ComparisonTotalsForLegs(
  legs: FlightLeg[],
  rates: Rates
): Q4ComparisonTotals {
  if (legs.length === 0) return emptyTotals();

  let fuel2x = 0;
  let exciseSum = 0;
  for (const leg of legs) {
    fuel2x += personalFuelOwed2x(leg, rates, leg.personalUse);
    exciseSum += legAirExcise(leg, rates);
  }
  exciseSum = money(exciseSum);

  const qMap = aggregateByQuarter(legs, rates, {
    isPersonalLeg: (l) => l.personalUse,
  });

  let domesticSegment = 0;
  let internationalFacility = 0;
  for (const qt of qMap.values()) {
    domesticSegment = money(domesticSegment + qt.irs26DomesticSegmentTax);
    internationalFacility = money(
      internationalFacility + qt.irs27InternationalFacilityTax
    );
  }

  const optPct = personalTransportPercentageTaxTotal(legs, rates, personal720);
  const grandTotal = money(fuel2x + exciseSum + optPct);

  return {
    legCount: legs.length,
    domesticSegment,
    internationalFacility,
    combinedAirExcise: exciseSum,
    twoXFuel: fuel2x,
    optionalPersonalPctTax: optPct,
    grandTotal,
  };
}

/**
 * Q4 2025 `[Time share]` legs only — same filter as the Comparison tab and
 * {@link fourQuarterTimeshareN426dh.logbook.test.ts}.
 */
export function computeQ4TimeShareComparisonTotals(
  allLegs: FlightLeg[],
  rates: Rates = DUNLEAVY_Q4_COMPARISON_RATES
): { filteredLegs: FlightLeg[]; totals: Q4ComparisonTotals } {
  const legs = filterQ4_2025TimeShareLegsFromLogbook(allLegs);
  if (legs.length === 0) {
    return { filteredLegs: [], totals: emptyTotals() };
  }
  return {
    filteredLegs: legs,
    totals: computeQ4ComparisonTotalsForLegs(legs, rates),
  };
}

/** IRS 26/27-style components summed over all calendar quarters present in <code>legs</code>. */
export type ExciseBreakdownByComponent = {
  irs26PercentageTax: number;
  irs26DomesticSegmentTax: number;
  irs27InternationalFacilityTax: number;
  irs26AkHiDepartureTax: number;
  combinedAirExcise: number;
};

export function computeExciseBreakdownForLegs(
  legs: FlightLeg[],
  rates: Rates
): ExciseBreakdownByComponent {
  const empty: ExciseBreakdownByComponent = {
    irs26PercentageTax: 0,
    irs26DomesticSegmentTax: 0,
    irs27InternationalFacilityTax: 0,
    irs26AkHiDepartureTax: 0,
    combinedAirExcise: 0,
  };
  if (legs.length === 0) return empty;
  const qMap = aggregateByQuarter(legs, rates);
  let pct = 0;
  let seg = 0;
  let intl = 0;
  let ak = 0;
  let comb = 0;
  for (const qt of qMap.values()) {
    pct = money(pct + qt.irs26PercentageTax);
    seg = money(seg + qt.irs26DomesticSegmentTax);
    intl = money(intl + qt.irs27InternationalFacilityTax);
    ak = money(ak + qt.irs26AkHiDepartureTax);
    comb = money(comb + totalExciseAir(qt));
  }
  return {
    irs26PercentageTax: pct,
    irs26DomesticSegmentTax: seg,
    irs27InternationalFacilityTax: intl,
    irs26AkHiDepartureTax: ak,
    combinedAirExcise: comb,
  };
}
